/**
 * Log an enquiry — the realtor records someone who reached them outside
 * Finishd (WhatsApp, a call, a referral, a walk-in). What the person said
 * becomes the first message of the enquiry's chat.
 */

import { useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateEnquiry } from "@/features/realtor/hooks/use-realtor-crm";
import { listMyListings } from "@/features/realtor/services/listings-api.service";
import {
	ENQUIRY_CHANNELS,
	type EnquiryChannel,
	type EnquiryDetail,
	type EnquiryInput,
} from "@/features/realtor/services/realtor-crm-api.service";
import { StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";
import {
	ChoiceChips,
	FormField,
	isAcceptablePhone,
	parseBudgetRange,
	SELECT_CLASS,
} from "./enquiry-shared";

interface LogEnquiryDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Called with the saved enquiry after the dialog has closed. */
	onLogged?: (detail: EnquiryDetail) => void;
}

export function LogEnquiryDialog({
	open,
	onOpenChange,
	onLogged,
}: LogEnquiryDialogProps): ReactElement {
	const { t } = useTranslation();
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-xl border-dashboard-border bg-dashboard-bg sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="font-display text-2xl font-medium italic text-dashboard-text">
						{t("realtor.enquiries.log.title")}
					</DialogTitle>
					<DialogDescription className="text-dashboard-muted">
						{t("realtor.enquiries.log.description")}
					</DialogDescription>
				</DialogHeader>
				{/* DialogContent unmounts when closed, so each opening starts blank. */}
				<LogEnquiryForm
					onCancel={() => onOpenChange(false)}
					onLogged={(detail) => {
						onOpenChange(false);
						onLogged?.(detail);
					}}
				/>
			</DialogContent>
		</Dialog>
	);
}

interface FormState {
	name: string;
	message: string;
	channel: EnquiryChannel;
	phone: string;
	listingId: string;
	budgetMinInput: string;
	budgetMaxInput: string;
	intent: string;
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

const EMPTY: FormState = {
	name: "",
	message: "",
	channel: "whatsapp",
	phone: "",
	listingId: "",
	budgetMinInput: "",
	budgetMaxInput: "",
	intent: "",
};

function LogEnquiryForm({
	onCancel,
	onLogged,
}: {
	onCancel: () => void;
	onLogged: (detail: EnquiryDetail) => void;
}): ReactElement {
	const { t } = useTranslation();
	const [form, setForm] = useState<FormState>(EMPTY);
	const [errors, setErrors] = useState<FieldErrors>({});
	const createEnquiry = useCreateEnquiry();
	const listings = useQuery({
		// Its own slice of the listings key: this caches the bare array the
		// picker needs, which another listings query may not share.
		queryKey: queryKeys.realtor.listings({ use: "enquiry-picker" }),
		queryFn: async () => {
			const result = await listMyListings();
			if (!result.success || !result.data) {
				throw new Error(result.error ?? "Failed to load listings");
			}
			return result.data;
		},
	});

	const setField = <K extends keyof FormState>(
		key: K,
		value: FormState[K],
	): void => {
		setForm((prev) => ({ ...prev, [key]: value }));
		setErrors((prev) => {
			if (!prev[key]) return prev;
			const next = { ...prev };
			delete next[key];
			return next;
		});
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		const nextErrors: FieldErrors = {};
		const name = form.name.trim();
		const message = form.message.trim();
		const phone = form.phone.trim();
		if (!name) nextErrors.name = t("realtor.enquiries.log.errors.name");
		if (!message)
			nextErrors.message = t("realtor.enquiries.log.errors.message");
		if (phone && !isAcceptablePhone(phone)) {
			nextErrors.phone = t("realtor.enquiries.errors.phone");
		}
		const budget = parseBudgetRange(form.budgetMinInput, form.budgetMaxInput);
		if (budget.minError) {
			nextErrors.budgetMinInput = t(
				`realtor.enquiries.errors.${budget.minError}`,
			);
		}
		if (budget.maxError) {
			nextErrors.budgetMaxInput = t(
				`realtor.enquiries.errors.${budget.maxError}`,
			);
		}
		setErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) return;

		const input: EnquiryInput = {
			name,
			message,
			channel: form.channel,
			phone: phone || null,
			listingId: form.listingId || null,
			budgetMin: budget.budgetMin,
			budgetMax: budget.budgetMax,
			intent: form.intent.trim() || null,
		};
		createEnquiry.mutate(input, {
			onSuccess: (detail) => {
				toast.success(t("realtor.enquiries.log.successToast"));
				onLogged(detail);
			},
		});
	};

	const listingOptions = listings.data ?? [];

	return (
		<form onSubmit={handleSubmit} noValidate className="space-y-4">
			<FormField
				id="enquiry-name"
				label={t("realtor.enquiries.log.nameLabel")}
				error={errors.name}
			>
				<Input
					id="enquiry-name"
					value={form.name}
					onChange={(e) => setField("name", e.target.value)}
					placeholder={t("realtor.enquiries.log.namePlaceholder")}
					maxLength={200}
					aria-invalid={errors.name ? true : undefined}
				/>
			</FormField>

			<FormField
				id="enquiry-message"
				label={t("realtor.enquiries.log.messageLabel")}
				error={errors.message}
			>
				<Textarea
					id="enquiry-message"
					value={form.message}
					onChange={(e) => setField("message", e.target.value)}
					placeholder={t("realtor.enquiries.log.messagePlaceholder")}
					rows={3}
					maxLength={5000}
					aria-invalid={errors.message ? true : undefined}
				/>
			</FormField>

			<ChoiceChips
				name="enquiry-channel"
				legend={t("realtor.enquiries.log.channelLabel")}
				value={form.channel}
				onChange={(channel) => setField("channel", channel)}
				options={ENQUIRY_CHANNELS.map((channel) => ({
					value: channel,
					label: t(`realtor.enquiries.channels.${channel}`),
				}))}
			/>

			<FormField
				id="enquiry-phone"
				label={t("realtor.enquiries.log.phoneLabel")}
				error={errors.phone}
			>
				<Input
					id="enquiry-phone"
					type="tel"
					inputMode="tel"
					value={form.phone}
					onChange={(e) => setField("phone", e.target.value)}
					placeholder="98765 43210"
					maxLength={20}
					aria-invalid={errors.phone ? true : undefined}
				/>
			</FormField>

			<FormField
				id="enquiry-listing"
				label={t("realtor.enquiries.log.listingLabel")}
				hint={
					listings.isError
						? t("realtor.enquiries.log.listingsFailed")
						: undefined
				}
			>
				<select
					id="enquiry-listing"
					className={SELECT_CLASS}
					value={form.listingId}
					onChange={(e) => setField("listingId", e.target.value)}
					disabled={listings.isPending}
				>
					<option value="">
						{listings.isPending
							? t("realtor.enquiries.log.listingsLoading")
							: t("realtor.enquiries.log.listingNone")}
					</option>
					{listingOptions.map((listing) => (
						<option key={listing.id} value={listing.id}>
							{listing.headline} · {listing.locality}
						</option>
					))}
				</select>
			</FormField>

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<FormField
					id="enquiry-budget-min"
					label={t("realtor.enquiries.budgetMinLabel")}
					error={errors.budgetMinInput}
					hint={t("realtor.enquiries.budgetHint")}
				>
					<Input
						id="enquiry-budget-min"
						value={form.budgetMinInput}
						onChange={(e) => setField("budgetMinInput", e.target.value)}
						placeholder="50L"
						aria-invalid={errors.budgetMinInput ? true : undefined}
					/>
				</FormField>
				<FormField
					id="enquiry-budget-max"
					label={t("realtor.enquiries.budgetMaxLabel")}
					error={errors.budgetMaxInput}
				>
					<Input
						id="enquiry-budget-max"
						value={form.budgetMaxInput}
						onChange={(e) => setField("budgetMaxInput", e.target.value)}
						placeholder="1.2 Cr"
						aria-invalid={errors.budgetMaxInput ? true : undefined}
					/>
				</FormField>
			</div>

			<FormField
				id="enquiry-intent"
				label={t("realtor.enquiries.log.intentLabel")}
			>
				<Input
					id="enquiry-intent"
					value={form.intent}
					onChange={(e) => setField("intent", e.target.value)}
					placeholder={t("realtor.enquiries.log.intentPlaceholder")}
					maxLength={200}
				/>
			</FormField>

			{createEnquiry.isError && (
				<p className="text-sm text-dashboard-pill-risk" role="alert">
					{createEnquiry.error.message}
				</p>
			)}

			<DialogFooter className="gap-2">
				<StudioButton variant="secondary" size="sm" onClick={onCancel}>
					{t("realtor.enquiries.cancel")}
				</StudioButton>
				<StudioButton
					type="submit"
					size="sm"
					disabled={createEnquiry.isPending}
				>
					{createEnquiry.isPending
						? t("realtor.enquiries.saving")
						: t("realtor.enquiries.log.submit")}
				</StudioButton>
			</DialogFooter>
		</form>
	);
}
